/*
 * WheelPicker (v242.31.0) — reusable single-column snap-scroll picker.
 *
 * Same mechanic and styling as DobPicker (one column instead of three): a
 * compact field that opens a branded bottom-sheet wheel. Used for medication
 * dose units and frequency, and general enough for any short option list.
 *
 * The sheet portals to document.body so its position:fixed escapes any
 * transformed ancestor (PageWrapper's framer-motion transform, the med-edit
 * modal, the onboarding add-sheet). z-index sits above the med modal (99999).
 *
 * Props:
 *   value      selected value (canonical — stored as-is)
 *   onChange   (value) => void, fired on Done
 *   options    array of string | { value, label }
 *   title      bottom-sheet header
 *   placeholder shown on the closed field when no value
 *   doneLabel  Done button text (default 'Done')
 *   fieldStyle style overrides for the closed field button
 *   valueColor closed-field text color (default navy)
 */
import { useRef, useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown } from 'lucide-react'

const H = 34

function normOpts(options) {
  return (options || []).map((o) => (typeof o === 'string' ? { value: o, label: o } : o))
}

export default function WheelPicker({
  value,
  onChange,
  options,
  title,
  placeholder = 'Select',
  doneLabel = 'Done',
  fieldStyle,
  valueColor = '#15354F',
}) {
  const [open, setOpen] = useState(false)
  const colRef = useRef(null)

  // Preserve an out-of-list current value (e.g. a legacy unit) by surfacing it
  // at the top of the list so it stays selectable and is never silently changed.
  let opts = normOpts(options)
  if (value != null && value !== '' && !opts.some((o) => o.value === value)) {
    opts = [{ value, label: String(value) }, ...opts]
  }

  const current = opts.find((o) => o.value === value)
  const display = current ? current.label : ''

  const idxOf = (el) => (el ? Math.round(el.scrollTop / H) : 0)

  const paint = (el) => {
    if (!el) return
    const s = idxOf(el)
    el.querySelectorAll('[data-wpitem]').forEach((it, i) => {
      it.className = 'wpitem' + (i === s ? ' sel' : (Math.abs(i - s) === 1 ? ' adj' : ''))
    })
  }

  useEffect(() => {
    if (!open) return
    const idx = Math.max(0, opts.findIndex((o) => o.value === value))
    const raf1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (colRef.current) colRef.current.scrollTop = idx * H
        paint(colRef.current)
      })
    })
    return () => cancelAnimationFrame(raf1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [open])

  const onScroll = (el) => {
    paint(el)
    clearTimeout(el._wpT)
    el._wpT = setTimeout(() => paint(el), 90)
  }

  const commit = () => {
    const i = Math.min(opts.length - 1, Math.max(0, idxOf(colRef.current)))
    const chosen = opts[i]
    if (chosen && onChange) onChange(chosen.value)
    setOpen(false)
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full flex items-center justify-between bg-white"
        style={fieldStyle || { height: 42, border: '1.5px solid #E2E8F0', borderRadius: 12, padding: '0 12px' }}
      >
        <span style={{ fontSize: 14, color: display ? valueColor : '#94A3B8', fontWeight: display ? 600 : 400 }}>
          {display || placeholder}
        </span>
        <ChevronDown size={17} style={{ color: '#46A8F9' }} />
      </button>

      {open && createPortal((
        <div
          onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 100000, background: 'rgba(15,31,46,0.32)', display: 'flex', alignItems: 'flex-end' }}
        >
          <style>{`
            .wpcol{height:170px;overflow-y:scroll;scroll-snap-type:y mandatory;scrollbar-width:none;text-align:center;}
            .wpcol::-webkit-scrollbar{display:none;}
            .wpitem{height:34px;line-height:34px;scroll-snap-align:center;font-size:14px;color:#AFC6DA;font-weight:500;transition:color .12s,font-size .12s;white-space:nowrap;}
            .wpitem.sel{color:#15354F;font-weight:700;font-size:17px;}
            .wpitem.adj{color:#6E93B4;font-size:15px;}
          `}</style>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: '100%', background: '#fff', borderRadius: '20px 20px 0 0', padding: '14px 16px calc(16px + env(safe-area-inset-bottom))' }}
          >
            <div style={{ width: 38, height: 4, borderRadius: 99, background: '#D8E6F0', margin: '0 auto 12px' }} />
            {title && (
              <div style={{ fontSize: 14, fontWeight: 700, color: '#15354F', textAlign: 'center', marginBottom: 12 }}>
                {title}
              </div>
            )}
            <div style={{ position: 'relative', height: 170 }}>
              <div style={{ position: 'absolute', left: 0, right: 0, top: 68, height: 34, background: '#EAF6FF', borderTop: '1.5px solid #A7D9FC', borderBottom: '1.5px solid #A7D9FC', borderRadius: 10, pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', left: 0, right: 0, top: 0, height: 56, background: 'linear-gradient(#fff,rgba(255,255,255,0))', pointerEvents: 'none', zIndex: 2 }} />
              <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 56, background: 'linear-gradient(rgba(255,255,255,0),#fff)', pointerEvents: 'none', zIndex: 2 }} />
              <div ref={colRef} className="wpcol" style={{ position: 'relative', zIndex: 1 }} onScroll={(e) => onScroll(e.currentTarget)}>
                <div style={{ height: 68 }} />
                {opts.map((o) => <div key={o.value} data-wpitem className="wpitem">{o.label}</div>)}
                <div style={{ height: 68 }} />
              </div>
            </div>
            <button
              type="button"
              onClick={commit}
              style={{ width: '100%', marginTop: 14, height: 46, border: 'none', borderRadius: 13, background: '#15354F', color: '#fff', fontSize: 14.5, fontWeight: 700 }}
            >
              {doneLabel}
            </button>
          </div>
        </div>
      ), document.body)}
    </div>
  )
}
